import { QRScannerDialog, QRScannerDialogRef } from '@/components/QRScannerDialog'
import { SectionCard } from '@/components/SectionCard'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useGetDepartmentItems } from '@/features/departments/hooks/useDepartment'
import { useCreateItemUsage, useEndItemUsage } from '@/features/items/hooks/useItem'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { ScanLine } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PatientItems } from '../../types/patient'

export function PatientItemsSection({
  departmentId,
  patientItems,
  testIdPrefix = 'bed-detail',
}: {
  departmentId?: string
  patientItems?: PatientItems
  testIdPrefix?: string
}) {
  const { t } = useTranslation()
  const confirm = useConfirm()

  const scannerRef = useRef<QRScannerDialogRef>(null)

  const createItemUsage = useCreateItemUsage()
  const endItemUsage = useEndItemUsage()

  const tp = testIdPrefix
  const selectTestId = `${tp}-equipment-select`
  const noteTestId = `${tp}-equipment-note`
  const addButtonTestId = `${tp}-add-equipment-button`
  const removeButtonTestId = `${tp}-remove-equipment-button`
  const patient = patientItems?.patient
  const items = patientItems?.items ?? []
  const patientId = patient?.id

  const { data: departmentItems, isFetching: isFetchingDepartmentItems } = useGetDepartmentItems(
    departmentId!,
    undefined,
    !!patientId,
  )

  const [form, setForm] = useState({ itemId: '', note: '' })

  const isInvalid = !form.itemId || !patientId
  const isLoading = createItemUsage.isPending || endItemUsage.isPending

  const patientAssignedItemIds = new Set(items.map(x => x.itemId))
  const filteredItems =
    departmentItems?.items?.filter(i => {
      if (i.status === 'Available') {
        return true
      }

      if (i.status === 'InUse') {
        return patientAssignedItemIds.has(i.id)
      }

      return false
    }) ?? []
  const assignedItems = filteredItems.filter(c => patientAssignedItemIds.has(c.id))
  const availableItems = filteredItems.filter(c => !patientAssignedItemIds.has(c.id))
  const selectedItemIncluded = patientAssignedItemIds.has(form.itemId)

  const selectedItem = departmentItems?.items?.find(item => item.id === form.itemId)
  const itemGroups = useMemo<SearchableSelectGroup[]>(
    () => [
      {
        label: t('common.added'),
        options: assignedItems.map(item => ({
          value: item.id,
          label: [item.productName, item.serialNumber].filter(Boolean).join(' - ') || '-',
        })),
      },
      {
        label: t('common.available'),
        options: availableItems.map(item => ({
          value: item.id,
          label: [item.productName, item.serialNumber].filter(Boolean).join(' - ') || '-',
        })),
      },
    ],
    [assignedItems, availableItems, t],
  )

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/equipment\/([^/]+)/)
        const itemId = match?.[1]

        if (!itemId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        const exists = filteredItems.some(item => item.id === itemId)
        if (!exists) {
          showError(t('common.qrScanner.equipmentNotFound'))
          return
        }

        setForm(prev => ({ ...prev, itemId }))
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [filteredItems, t],
  )

  function handleUsageItem() {
    if (isInvalid) return
    const options = getMutationOptions({
      successMessage: t('patient.toast.addEquipmentSuccess'),
      onSuccess: () => setForm({ itemId: '', note: '' }),
    })

    const payload = {
      ...form,
      patientId: patientId,
      note: form.note.trim(),
    }

    createItemUsage.mutate({ id: form.itemId, payload }, options)
  }

  async function handleEndUsageItem() {
    if (isInvalid) return

    const item = items.find(i => i.itemId === form.itemId)
    const name = `${item?.productName} ${item?.itemSerialNumber}`
    const confirmed = await confirm({
      variant: 'destructive',
      title: `${t('patient.detail.equipmentSection.confirmRemoveTitle')}`,
      description: `${t('patient.detail.equipmentSection.confirmRemoveDescription', { name })}`,
    })
    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('patient.toast.removeEquipmentSuccess'),
        onSuccess: () => setForm({ itemId: '', note: '' }),
      })

      const payload = {
        ...form,
        note: form.note.trim(),
      }

      endItemUsage.mutate({ id: form.itemId, payload }, options)
    }
  }
  return (
    <>
      <SectionCard
        title={t('patient.detail.manageEquipment')}
        cardClass="mt-4"
        headerClass="pb-0"
        right={
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => scannerRef.current?.start()}
            disabled={isFetchingDepartmentItems}
            title={t('common.qrScanner.scanButton')}
          >
            <ScanLine className="size-4" />
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {!patientId ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {t('patient.detail.equipmentSection.requiredPatient')}
            </p>
          ) : !departmentId ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {t('patient.detail.equipmentSection.requiredBed')}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{t('common.equipment')} *</Label>
                  <SearchableSelect
                    value={form.itemId}
                    onValueChange={itemId => setForm({ ...form, itemId })}
                    groups={itemGroups}
                    placeholder={t('common.placeholder.selectEquipment')}
                    searchPlaceholder={t('common.placeholder.searchEquipment')}
                    emptyText={t('repairs.noEquipment')}
                    triggerClassName="h-9 bg-secondary/50 w-full"
                    testId={selectTestId}
                  />
                  {selectedItem && (
                    <SelectedEquipmentCard
                      name={selectedItem.productName}
                      serialNumber={selectedItem.serialNumber}
                      departmentName={selectedItem.departmentName}
                      buildingName={selectedItem.buildingName}
                      floorName={selectedItem.floorName}
                    />
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{t('scans.field.note')}</Label>
                  <Textarea
                    value={form.note}
                    onChange={e => setForm({ ...form, note: e.target.value })}
                    className="bg-secondary/50 text-xs"
                    rows={3}
                    data-testid={noteTestId}
                    placeholder={t('common.placeholder.note')}
                  />
                </div>
              </div>

              <Button
                variant={selectedItemIncluded ? 'destructive' : 'default'}
                className="text-xs"
                data-testid={selectedItemIncluded ? removeButtonTestId : addButtonTestId}
                disabled={isInvalid || isLoading}
                onClick={selectedItemIncluded ? handleEndUsageItem : handleUsageItem}
              >
                {selectedItemIncluded ? t('common.remove') : t('common.add')}
                {isLoading && <Spinner />}
              </Button>
            </>
          )}
        </div>
      </SectionCard>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
